import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import {
  LOOKUP_META,
  OTHER_SETTINGS_KINDS,
  OTHER_SETTINGS_LINKS,
  RECORD_STATUSES,
} from '@app/shared';
import type { LookupDto, RecordStatus } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ForbiddenState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useAuth } from '../auth/AuthProvider';
import { SettingsListPage } from './SettingsListPage';
import { useCreateLookup, useLookups, useUpdateLookup } from './api';

/** The lists of this page; the equipment lists of the same store have their own screens. */
type LookupKind = (typeof OTHER_SETTINGS_KINDS)[number];

/**
 * What differs between the three lists, as read from the reference (product inventory,
 * appendix 11.5). The reasons have a Yes / No status and a numbered list of 20 per page; note
 * types have Active / Inactive, a note text, and a filter above the list.
 */
interface Screen {
  /** Heading of the form. */
  addTitle: string;
  /** Label of the name field and header of its column. */
  nameLabel: string;
  listTitle: (total: number) => string;
  /** Yes / No status (reasons) or Active / Inactive (note types). */
  yesNo: boolean;
  /** Suspend reasons have a "Display" switch. */
  display: boolean;
  /** Note types carry a note text. */
  notes: boolean;
  submitLabel: string;
  pageSize: number;
  emptyText?: string;
}

const SCREENS: Record<LookupKind, Screen> = {
  disconnectReason: {
    addTitle: 'Add Disconnection Reason',
    nameLabel: 'Disconnection Reason',
    listTitle: (n) => `Disconnection Reason(s) List : ${n} (Records)`,
    yesNo: true,
    display: false,
    notes: false,
    submitLabel: 'Add',
    pageSize: 20,
  },
  suspendReason: {
    addTitle: 'Add Suspend Reason',
    nameLabel: 'Suspend Reason',
    // The reference list was empty when read; its heading and columns follow the disconnection list.
    listTitle: (n) => `Suspend Reason(s) List : ${n} (Records)`,
    yesNo: true,
    display: true,
    notes: false,
    submitLabel: 'Add',
    pageSize: 20,
    emptyText: 'No record found !!',
  },
  noteType: {
    addTitle: 'Add Note Type',
    nameLabel: 'Note Type',
    listTitle: () => 'All Note Type',
    yesNo: false,
    display: false,
    notes: true,
    submitLabel: 'Submit',
    pageSize: 20,
  },
};

const FORM_ID = 'other-settings-entry-form';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

interface FormValues {
  name: string;
  /** '' until chosen on the reason forms, whose select starts at "--Select--". */
  status: RecordStatus | '';
  display: 'yes' | 'no' | '';
  notes: string;
}

const emptyForm = (screen: Screen): FormValues => ({
  name: '',
  status: screen.yesNo ? '' : 'Active',
  display: screen.display ? '' : 'yes',
  notes: '',
});

function EntryForm(props: { kind: LookupKind; editing: LookupDto | null; onDone: () => void }) {
  const { kind, editing, onDone } = props;
  const screen = SCREENS[kind];
  const notify = useNotify();
  const createLookup = useCreateLookup(kind);
  const updateLookup = useUpdateLookup(kind);
  const { control, handleSubmit, reset, setError } = useForm<FormValues>({
    defaultValues: emptyForm(screen),
  });

  useEffect(() => {
    reset(
      editing
        ? {
            name: editing.name,
            status: editing.status,
            display: editing.display ? 'yes' : 'no',
            notes: editing.notes,
          }
        : emptyForm(screen),
    );
  }, [editing, reset, screen]);

  const submit = handleSubmit(async (values) => {
    let invalid = false;
    const need = (field: keyof FormValues, message: string) => {
      setError(field, { type: 'required', message });
      invalid = true;
    };
    if (!values.name.trim()) need('name', `${screen.nameLabel} is required`);
    if (!values.status) need('status', 'Select Status');
    if (screen.display && !values.display) need('display', 'Select Display');
    if (screen.notes && !values.notes.trim()) need('notes', 'Notes is required');
    if (invalid || !values.status) return;

    const input = {
      name: values.name.trim(),
      status: values.status,
      display: values.display !== 'no',
      notes: values.notes.trim(),
    };
    try {
      if (editing) {
        await updateLookup.mutateAsync({ id: editing.id, input });
        notify.success(`${screen.nameLabel} updated.`);
      } else {
        await createLookup.mutateAsync(input);
        notify.success(`${screen.nameLabel} added.`);
      }
      reset(emptyForm(screen));
      onDone();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const busy = createLookup.isPending || updateLookup.isPending;
  // The reference opens the same form to modify an entry; its edit heading was not recorded.
  const title = editing ? screen.addTitle.replace(/^Add/, 'Update') : screen.addTitle;
  const statusOptions = screen.yesNo
    ? [
        { value: 'Active', label: 'Yes' },
        { value: 'Inactive', label: 'No' },
      ]
    : toOptions(RECORD_STATUSES);

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      onSubmit={(e) => void submit(e)}
      aria-label={title}
      id={FORM_ID}
      sx={{ p: 2, mb: 2 }}
    >
      <Typography variant="h3" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: screen.notes ? '1fr 2fr 1fr' : '2fr 1fr 1fr' },
          alignItems: 'start',
        }}
      >
        <TextInput
          control={control}
          name="name"
          label={screen.nameLabel}
          required
          multiline={screen.yesNo}
          minRows={screen.yesNo ? 2 : undefined}
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
        {screen.notes ? (
          <TextInput
            control={control}
            name="notes"
            label="Notes"
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
        ) : null}
        <SelectInput
          control={control}
          name="status"
          label="Status"
          required={screen.yesNo}
          emptyLabel={screen.yesNo ? '--Select--' : undefined}
          options={statusOptions}
        />
        {screen.display ? (
          <SelectInput
            control={control}
            name="display"
            label="Display"
            required
            emptyLabel="--Select--"
            options={YES_NO}
          />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button type="submit" variant="contained" disabled={busy}>
          {editing ? 'Update' : screen.submitLabel}
        </Button>
        {editing ? (
          <Button type="button" variant="outlined" disabled={busy} onClick={onDone}>
            Cancel
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}

const yn = (on: boolean) => (on ? 'Y' : 'N');

function ManagedList({ kind }: { kind: LookupKind }) {
  const screen = SCREENS[kind];
  const list = useListState(
    { q: '', status: '' },
    screen.pageSize,
    screen.notes ? 'number' : 'name',
  );
  const items = useLookups(kind, list.params);
  const [editing, setEditing] = useState<LookupDto | null>(null);
  const total = items.data?.total ?? 0;

  const edit = (r: LookupDto, label: string) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={() => {
          setEditing(r);
          // The form is above the list; bring it into view (not every browser has smooth scrolling).
          document.getElementById(FORM_ID)?.scrollIntoView?.({ block: 'start' });
        }}
        aria-label={`${label} ${r.name}`}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const columns: Column<LookupDto>[] = screen.notes
    ? [
        { key: 'number', header: 'Id', sortable: true, width: 70, render: (r) => r.number },
        { key: 'name', header: 'Type', sortable: true, render: (r) => r.name },
        { key: 'notes', header: 'Notes', render: (r) => r.notes },
        {
          key: 'status',
          header: 'Status',
          sortable: true,
          render: (r) => <StatusChip status={r.status} />,
        },
        { key: 'action', header: 'Action', render: (r) => edit(r, 'Edit') },
      ]
    : [
        {
          key: '#',
          header: '#',
          width: 50,
          render: (_r, i) => rowNumber(i, list.page, list.pageSize),
        },
        { key: 'name', header: screen.nameLabel, sortable: true, render: (r) => r.name },
        {
          key: 'status',
          header: 'Status',
          sortable: true,
          render: (r) => yn(r.status === 'Active'),
        },
        ...(screen.display
          ? [{ key: 'display', header: 'Display', render: (r: LookupDto) => yn(r.display) }]
          : []),
        { key: 'createdBy', header: 'Added By', render: (r) => r.createdBy?.loginId ?? '' },
        { key: 'updatedBy', header: 'Updated By', render: (r) => r.updatedBy?.loginId ?? '' },
        {
          key: 'updatedAt',
          header: 'Created/Updated Datetime',
          sortable: true,
          nowrap: true,
          render: (r) => formatDateTime(r.updatedAt),
        },
        { key: 'option', header: 'Option', render: (r) => edit(r, 'Modify') },
      ];

  return (
    <Box>
      <EntryForm kind={kind} editing={editing} onDone={() => setEditing(null)} />

      <Typography variant="h3" sx={{ mb: 1 }}>
        {screen.listTitle(total)}
      </Typography>
      {screen.notes ? (
        <FilterPanel onSearch={list.search} onReset={list.reset}>
          <TextField
            select
            label="Status"
            value={list.draft.status}
            onChange={(e) => list.setField('status', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {RECORD_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search Notes"
            value={list.draft.q}
            onChange={(e) => list.setField('q', e.target.value)}
          />
        </FilterPanel>
      ) : null}
      <DataTable
        label={LOOKUP_META[kind].title}
        columns={columns}
        rows={items.data?.data}
        rowKey={(r) => r.id}
        loading={items.isFetching}
        error={items.error}
        onRetry={() => void items.refetch()}
        emptyText={screen.emptyText}
        page={list.page}
        pageSize={list.pageSize}
        total={total}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />
    </Box>
  );
}

/** A settings list of Other Settings, by the key in the address. */
export function SettingsListByKeyPage() {
  const { key } = useParams();
  return (
    <SettingsListPage
      listKey={key ?? ''}
      back={{ to: '/settings/other', label: 'Other Settings' }}
    />
  );
}

/**
 * Other Settings (PC240): the landing page with the reference's twelve links, and the three
 * managed lists of this file (reasons and note types). The other screens have their own
 * components: settings lists, the bulk upload of notes and the note report.
 */
export function OtherSettingsPage() {
  const { can } = useAuth();
  const { kind } = useParams();

  if (!kind) {
    const allowed = OTHER_SETTINGS_LINKS.filter((l) => can(l.code));
    if (allowed.length === 0)
      return (
        <ForbiddenState code={[...new Set(OTHER_SETTINGS_LINKS.map((l) => l.code))].join(' / ')} />
      );
    return (
      <Box>
        <PageHeader title="Other Settings" code="PC240" />
        <Paper variant="outlined" component="nav" aria-label="Other Settings" sx={{ p: 2 }}>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 1 }}>
            {allowed.map((l) => (
              <li key={l.path}>
                <MuiLink component={Link} to={l.path}>
                  {l.title} ({l.code})
                </MuiLink>
              </li>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  const current = OTHER_SETTINGS_KINDS.find((k) => k === kind);
  if (!current) return <Alert severity="warning">This list does not exist.</Alert>;
  if (!can(LOOKUP_META[current].code)) return <ForbiddenState code={LOOKUP_META[current].code} />;
  return (
    <Box>
      <PageHeader
        title={LOOKUP_META[current].title}
        code={LOOKUP_META[current].code}
        actions={
          <Button component={Link} to="/settings/other" variant="outlined">
            Other Settings
          </Button>
        }
      />
      {/* `key` gives each list its own form, filters and paging. */}
      <ManagedList key={current} kind={current} />
    </Box>
  );
}
